'use client';
import React from 'react';

// Skeleton primitives
export function SkeletonLine({ className = '' }: { className?: string }) {
  return <div className={`bg-surface-3 rounded animate-pulse ${className}`} />;
}

export function SkeletonCard({ className = '' }: { className?: string }) {
  return (
    <div className={`bg-white rounded-xl border border-border p-5 space-y-3 ${className}`}>
      <SkeletonLine className="h-3 w-24" />
      <SkeletonLine className="h-7 w-16" />
      <SkeletonLine className="h-2.5 w-32" />
    </div>
  );
}

export function SkeletonRow({ className = '' }: { className?: string }) {
  return (
    <div className={`bg-white rounded-xl border border-border p-4 flex items-center gap-4 ${className}`}>
      <div className="w-10 h-10 rounded-full bg-surface-3 animate-pulse flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <SkeletonLine className="h-3.5 w-48" />
        <SkeletonLine className="h-2.5 w-32" />
      </div>
      <SkeletonLine className="h-6 w-16 rounded-full" />
    </div>
  );
}

export function SkeletonDashboard() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {[...Array(4)].map((_, i) => <SkeletonRow key={i} />)}
        </div>
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="bg-white rounded-xl border border-border overflow-hidden">
      <div className="p-4 border-b border-border flex gap-4">
        <SkeletonLine className="h-3 w-24" />
        <SkeletonLine className="h-3 w-20" />
        <SkeletonLine className="h-3 w-28" />
      </div>
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="p-4 border-b border-border last:border-0 flex items-center gap-4">
          <SkeletonLine className="h-3 w-32" />
          <SkeletonLine className="h-3 w-24" />
          <SkeletonLine className="h-3 w-20 ml-auto" />
          <SkeletonLine className="h-6 w-16 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonFormStep() {
  return (
    <div className="space-y-5 animate-pulse">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="space-y-2">
          <SkeletonLine className="h-3 w-28" />
          <SkeletonLine className="h-11 w-full rounded-xl" />
        </div>
      ))}
    </div>
  );
}
